import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  LinearProgress,
  Typography,
  Link,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import useGetChangeTickets from '../hooks/vendors/useGetChangeTickets';
import VendorStatusBadge from '../vendors/VendorStatusBadge';
import { StandardVendorNameCell, TicketActionsCell } from '../vendors/VendorChangeTicketCells';
import { VendorChangeTicketEntry } from '../../types/vendor-types';

interface VendorChangeTicketDialogProps {
  open: boolean;
  onClose: () => void;
  normalizedVendorName: string;
}

const VendorChangeTicketDialog: React.FC<VendorChangeTicketDialogProps> = ({
  open,
  onClose,
  normalizedVendorName,
}) => {
  // Fetch pending tickets (only when dialog is open)
  const {
    data: pendingTicketsResponse,
    isLoading: isLoadingPending,
    isError: isErrorPending,
  } = useGetChangeTickets({
    status: 'pending',
    limit: 50,
    offset: 0,
    normalizedVendorName: normalizedVendorName,
    enabled: open,
  });

  // Fetch rejected tickets (only when dialog is open)
  const {
    data: rejectedTicketsResponse,
    isLoading: isLoadingRejected,
    isError: isErrorRejected,
  } = useGetChangeTickets({
    status: 'rejected',
    limit: 50,
    offset: 0,
    normalizedVendorName: normalizedVendorName,
    enabled: open,
  });

  const isLoading = isLoadingPending || isLoadingRejected;
  const isError = isErrorPending || isErrorRejected;

  // Combine pending and rejected tickets
  const changeTickets = [
    ...(pendingTicketsResponse?.results || []),
    ...(rejectedTicketsResponse?.results || []),
  ];

  return (
    <DialogGlynt
      open={open}
      closeModal={onClose}
      loading={false}
      cypressId="dialog-vendor-change-tickets"
      width="850px"
    >
      <DialogTitleGlynt title="Vendor Change Tickets" closeModal={onClose} />
      <DialogContentGlynt scrollable>
        <Box sx={{ mb: 2 }}>
          <Link
            href={`/vendors?tab=change-tickets&vendor_name=${encodeURIComponent(normalizedVendorName)}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            underline="hover"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
            data-cy="link-vendor-change-tickets-page"
          >
            Open in Vendors page
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </Link>
        </Box>
        {isError ? (
          <Typography color="error">Error loading change tickets.</Typography>
        ) : isLoading ? (
          <LinearProgress />
        ) : changeTickets.length === 0 ? (
          <Typography sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
            No change tickets found for this vendor.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Normalized Vendor Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Set Standard Vendor Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody data-cy="tablebody-vendor-change-tickets-dialog">
              {changeTickets.map((ticket: VendorChangeTicketEntry) => (
                <TableRow key={ticket.id}>
                  <TableCell>{ticket.normalized_vendor_name}</TableCell>
                  <TableCell>
                    <StandardVendorNameCell ticket={ticket} />
                  </TableCell>
                  <TableCell align="center">
                    <VendorStatusBadge status={ticket.status || 'pending'} />
                  </TableCell>
                  <TableCell align="right">
                    <TicketActionsCell ticket={ticket} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContentGlynt>
    </DialogGlynt>
  );
};

export default VendorChangeTicketDialog;
